import React from "react";
import { useSidebar } from "../../context/sidebarContext";

export default function LearnMoreLayout({
  title,
  breadcrumb = [],
  sections = [],
}) {
  const { isMobile, isTablet } = useSidebar();
  // Check if "Home" is already the first item in the data passed
  const hasHome = breadcrumb.length > 0 && breadcrumb[0].label === "Home";

  // Only add Home if it's missing
  const allBreadcrumbs = hasHome
    ? breadcrumb
    : [
        { label: "Home", onClick: () => (window.location.href = "/") },
        ...breadcrumb,
      ];

  return (
    <div className="min-h-screen bg-[#F2F4F8]">
      <div className="container mx-auto px-4 lg:px-6 py-8 pt-24 lg:py-12 lg:pt-28">
        {/* BREADCRUMB */}
        <nav className="pl-2 text-xs sm:text-sm text-[#0E0F11] mb-6">
          {allBreadcrumbs.map((item, idx) => {
            const isLast = idx === allBreadcrumbs.length - 1;

            return (
              <span key={item.label}>
                {!isLast ? (
                  <>
                    <span
                      className="cursor-pointer hover:underline hover:text-gray-700 transition-colors"
                      onClick={item.onClick}
                    >
                      {item.label}
                    </span>
                    <span className="mx-1.5 text-gray-400">/</span>
                  </>
                ) : (
                  <span className="text-[#0E0F11]/65 font-medium">
                    {item.label}
                  </span>
                )}
              </span>
            );
          })}
        </nav>

        {/* MAIN LAYOUT */}
        <div className="grid gap-6 lg:gap-10 lg:grid-cols-[260px,1fr] items-start">
          {/* LEFT SIDEBAR NAV - STICKY */}
          {/* 'sticky top-8' makes it stick. 'self-start' ensures it doesn't stretch full height */}
          <aside className="hidden lg:block sticky top-20 self-start">
            <div className="">
              <nav className="flex flex-col">
                {sections.map((section, idx) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    // Styling to match the screenshot: borders between items, gray text
                    className={`
                      block py-4 px-2 text-sm font-medium border-b border-gray-200 transition-colors
                      ${
                        idx === 0
                          ? "text-[#000000] font-semibold"
                          : "text-[#7C7C7C] hover:text-gray-900"
                      }
                    `}
                  >
                    {section.navLabel || section.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* RIGHT CONTENT AREA */}

          <main className="space-y-4">
            <div
              className="w-full h-[145px] sm:h-[150px] md:h-[125px] lg:h-[110px] xl:h-[100px] 2xl:h-[130px] text-white font-bold flex items-center justify-center rounded-lg"
              style={{
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundImage: isMobile
                  ? "url('/images/backgrounds/bg-mobile-banner-learn-more.png')"
                  : isTablet
                  ? "url('/images/backgrounds/bg-tablet-banner-learn-more.png')"
                  : "url('/images/backgrounds/bg-banner-learn-more.png')",
              }}
            >
              <span className="text-xl md:text-2xl">{title}</span>
            </div>
            {sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 "
              >
                {/* Title inside the card matches the screenshot design */}
                {!section.hideTitle && (
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
                    {section.title}
                  </h2>
                )}

                {/* Content */}
                <div className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {section.content}
                </div>
              </section>
            ))}
          </main>
        </div>
      </div>
    </div>
  );
}
